/**
 * Switching workspace (customer-portal review 2026-09-25 #148).
 *
 * "Switch to" was disabled ("arrives soon"), the top-bar menu's other workspaces were enabled
 * buttons that did nothing, and the page still told people to "switch between them from the top of
 * the page" — so a new workspace could be made and filled with people, and never used.
 *
 * The route existed all along: `POST /v1/session/account` (`switchAuthSessionAccount`, design
 * §15.4.4) re-scopes the auth_session to one of the person's workspaces and ROTATES it. This hook is
 * the one way every control switches: the top-bar menu and the Workspaces cards call it; the
 * sidebar's workspace button opens Workspaces, where each card offers it.
 *
 * Every figure the console holds belongs to the workspace it was read in, so after the switch the
 * whole query cache is reset — nothing from the old workspace can be shown under the new one's
 * name — and the person lands on the dashboard, the first page of any workspace.
 *
 * AMENDED 2026-10-05 (plan-014 A4): a thin wrapper over the scope guard's one switch
 * (`../scope/guard.tsx`, `useSwitchScope`), which sends the current scope generation, tells the other
 * tabs, asks first when a tab holds unsaved edits, cancels and clears every query, closes every
 * stream and drops scope-bound drafts. Its shape is unchanged for the Workspaces page.
 */
import { switchProblemText, useSwitchScope } from "../scope/guard.js";

export interface WorkspaceSwitch {
  /** Re-scopes the sign-in to `workspaceId` in the same account, then opens its dashboard. */
  readonly switchTo: (workspaceId: string) => void;
  readonly pending: boolean;
  /** The plain-words reason the last switch failed, or `null`. */
  readonly problem: string | null;
}

export function useSwitchWorkspace(accountId: string | null): WorkspaceSwitch {
  const scope = useSwitchScope();
  return {
    switchTo: (workspaceId) => {
      // The Workspaces page reads the organization from the same auth_session the shell has already
      // read, so a missing one means the page is drawn before its own guard ran; nothing to switch.
      if (accountId === null) return;
      scope.switchTo({ accountId, workspaceId });
    },
    pending: scope.busy || scope.pending !== null,
    problem: scope.problem === null ? null : switchProblemText(scope.problem),
  };
}
